import {
    Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, List,
    ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Typography
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import Check from "@mui/icons-material/Check";
import { useAsyncAction } from "nate-react-api-helpers";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/API";
import { allWorkspace, switchPath, teamName, useWorkspace } from "./Workspace";

type Option = {
    key: string;
    label: string;
    selected: boolean;
}

// useWorkspaceOptions is what both switchers list: "All", "Personal", then the other
// teams, and the way to go to one
function useWorkspaceOptions() {
    const ws = useWorkspace();
    const location = useLocation();
    const navigate = useNavigate();

    const options: Option[] = [{ key: allWorkspace, label: "All", selected: ws.team === 0 }]
        .concat(ws.teams.map(team => ({
            key: String(team.id),
            label: teamName(team, team.id),
            selected: team.id === ws.team,
        })));

    return {
        ws: ws,
        options: options,
        go: (key: string) => navigate(switchPath(location, key)),
    };
}

// NewTeamDialog makes a team with the user as its first member, and opens its
// settings page
export function NewTeamDialog(props: { open: boolean; onClose: () => void }) {
    const ws = useWorkspace();
    const navigate = useNavigate();
    const [name, setName] = useState("");

    const create = useAsyncAction(async (input: { name: string }) => {
        const team = await api.teams.upsert({ name: input.name });
        ws.reload();
        setName("");
        props.onClose();
        navigate("/w/" + team.id + "/team");
    }, []);

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="xs" fullWidth>
            <form onSubmit={e => {
                e.preventDefault();
                if (name.trim()) create.callback({ name: name.trim() });
            }}>
                <DialogTitle>New team</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth label="Team name" margin="dense"
                               value={name} onChange={e => setName(e.target.value)} />
                    {create.LoadingElement}
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={create.loading || !name.trim()}>
                        Create
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

// WorkspaceSwitcher sits at the top of the nav
export function WorkspaceSwitcher(props: { onNavigate: () => void }) {
    const { ws, options, go } = useWorkspaceOptions();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [creating, setCreating] = useState(false);

    return (
        <>
            <ListItemButton onClick={e => setAnchor(e.currentTarget)}
                            sx={{ borderBottom: 1, borderColor: "divider" }}>
                <ListItemText
                    primary={ws.name}
                    secondary="Workspace"
                    slotProps={{
                        primary: { noWrap: true, sx: { fontWeight: 500 } },
                        secondary: { variant: "caption" },
                    }}
                />
                <ArrowDropDown fontSize="small" color="action" />
            </ListItemButton>

            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {options.map(option => (
                    <MenuItem key={option.key} selected={option.selected} onClick={() => {
                        setAnchor(null);
                        props.onNavigate();
                        go(option.key);
                    }}>
                        <ListItemIcon>{option.selected && <Check fontSize="small" />}</ListItemIcon>
                        {option.label}
                    </MenuItem>
                ))}
                <MenuItem onClick={() => {
                    setAnchor(null);
                    setCreating(true);
                }}>
                    <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                    New team
                </MenuItem>
            </Menu>

            <NewTeamDialog open={creating} onClose={() => {
                setCreating(false);
                props.onNavigate();
            }} />
        </>
    );
}

// WorkspaceChip names the workspace in the phone app bar, and opens a bottom sheet to
// change it
export function WorkspaceChip() {
    const { ws, options, go } = useWorkspaceOptions();
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);

    return (
        <>
            <ButtonBase onClick={() => setOpen(true)} sx={{
                display: "flex", alignItems: "center", gap: 0.25,
                maxWidth: 160, minWidth: 0,
                px: 1, py: 0.5, borderRadius: 4,
                bgcolor: "rgba(255, 255, 255, 0.16)",
            }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{ws.name}</Typography>
                <ArrowDropDown sx={{ fontSize: 18, flexShrink: 0 }} />
            </ButtonBase>

            <Drawer anchor="bottom" open={open} onClose={() => setOpen(false)}
                    slotProps={{ paper: { sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12 } } }}>
                <Box sx={{ pb: 1 }}>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pt: 2, pb: 1 }}>
                        Workspace
                    </Typography>
                    <List disablePadding>
                        {options.map(option => (
                            <ListItemButton key={option.key} selected={option.selected} onClick={() => {
                                setOpen(false);
                                go(option.key);
                            }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    {option.selected && <Check fontSize="small" />}
                                </ListItemIcon>
                                <ListItemText primary={option.label} />
                            </ListItemButton>
                        ))}
                        <ListItemButton onClick={() => {
                            setOpen(false);
                            setCreating(true);
                        }}>
                            <ListItemIcon sx={{ minWidth: 36 }}><Add fontSize="small" /></ListItemIcon>
                            <ListItemText primary="New team" />
                        </ListItemButton>
                    </List>
                </Box>
            </Drawer>

            <NewTeamDialog open={creating} onClose={() => setCreating(false)} />
        </>
    );
}
